import clsx from "clsx";
import { useMemo, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { describeError } from "../api/http";
import { usePlan, usePlayer, useUpdatePlan } from "../api/queries";
import type {
  Listing,
  Metrics,
  NextFixture,
  PlayerDetail,
  PlayerRound,
  PricePoint,
} from "../api/types";
import { Page } from "../components/layout/Page";
import { MetricBadge, MetricGrid } from "../components/MetricBadge";
import { PlayerAvatar } from "../components/PlayerAvatar";
import { Stat } from "../components/Scoreline";
import { Button, ButtonLink } from "../components/ui/Button";
import { PositionChip, StatusChip, STATUS_LABELS } from "../components/ui/Chip";
import { Banner, EmptyState, ErrorState } from "../components/ui/ErrorState";
import { Skeleton } from "../components/ui/Skeleton";
import {
  count,
  DASH,
  decimal,
  delta,
  diasEntre,
  fechaCorta,
  fechaDia,
  haVencido,
  money,
  percent,
  percentDelta,
  points,
  restante,
} from "../lib/format";
import {
  addToPlan,
  planSideOf,
  removeFromPlan,
  toPlanDraft,
  type PlanDraft,
  type PlanSide,
} from "../lib/plan";
import { POSITION_NAMES } from "../lib/positions";
import { EtiquetaClausula, vendedor } from "./Market";

/**
 * Pantalla *Ficha de jugador* — M8-T04 (UC-08.04).
 *
 * QUÉ ENSEÑA
 *   Todo lo que `GET /api/players/:id` sabe de un jugador, de arriba abajo y en una sola columna:
 *   quién es (posición, equipo, estado), sus tres cifras de cabecera, la rejilla de métricas del
 *   motor, su temporada jornada a jornada, cómo se ha movido su precio, contra quién juega la
 *   próxima jornada y —al final— la única acción de la pantalla: lo que se puede planear con él.
 *
 * ES UNA RUTA COMPLETA, NO UN MODAL
 *   Lo fija la sección 8.1: `/jugador/:id` con botón atrás. Así la ficha es enlazable y el botón
 *   atrás del navegador devuelve a la lista de la que se vino, con su scroll y sus filtros.
 *
 * UNA SOLA PETICIÓN
 *   `usePlayer` trae jugador, jornadas, precios, próximo partido, si es mío y si está en el
 *   mercado de una vez. `GET /api/plan` es la segunda y última: hace falta porque `PUT /api/plan`
 *   manda el plan **entero**, así que la acción está deshabilitada hasta haberlo leído —guardar
 *   antes borraría lo que hubiera dentro— y el lado se decide con `planSideOf`, nunca con el campo
 *   `inPlan` de la venta, que solo sabe de compras.
 *
 * LAS DOS GRÁFICAS SON RECHARTS, Y NINGUNA DISTINGUE NADA SOLO POR COLOR
 *   Lo pide la sección 2.1 (Recharts es del stack) y la aceptación de esta tarea. Los tres casos
 *   de una jornada —jugó, no jugó, su equipo no jugó— se diferencian por **forma** antes que por
 *   color: barra llena, tocón bajo, y caja vacía de trazo discontinuo. La leyenda los nombra por
 *   escrito y, debajo, un resumen invisible para el lector de pantalla dice cuántas hay de cada,
 *   porque una gráfica a solas no es accesible.
 *
 * POR QUÉ LAS GRÁFICAS NO SE ANIMAN
 *   La sección 8.1 exige respetar `prefers-reduced-motion`. Recharts no lo consulta: sus
 *   animaciones se apagan con una propiedad o no se apagan. Se apagan siempre —una barra que crece
 *   no aporta nada a un histórico— y así la gráfica se lee igual en cualquier ajuste del sistema.
 *
 * POR QUÉ CABEN A 360 PX
 *   `ResponsiveContainer` mide el hueco y dibuja dentro de él: el ancho no se escribe en ningún
 *   sitio, así que no hay ningún número que pueda desbordar la tarjeta. El alto sí es fijo, y es
 *   **el mismo** que reserva `Skeleton variant="chart"`, de modo que al llegar los datos la página
 *   no da un salto (sección 8.2).
 *
 * LO QUE SE TOMA PRESTADO DEL MERCADO
 *   `vendedor` y `EtiquetaClausula` viven en `pages/Market.tsx` y se importan de ahí. Son
 *   vocabulario del mercado —«Sistema», «Otro participante», «Cláusula»—: dos copias acabarían
 *   diciendo lo mismo de dos maneras distintas. Igual que `fechaDia` y `diasEntre` se han subido a
 *   `lib/format.ts` en vez de escribirse aquí.
 *
 * DÓNDE SE APARTA DEL MOCKUP
 *   `docs/mockups/jugador.html` dibuja doce jornadas y llama *Score* a la puntuación del
 *   recomendador. Aquí se pintan **todas** las jornadas que devuelve el servidor —el histórico
 *   entero es lo que se viene a ver, y `ResponsiveContainer` las reparte quepan doce o treinta y
 *   ocho— y la métrica se llama *Puntuación*, porque la sección 8.3 no admite jerga técnica en la
 *   interfaz. El mockup también escribe *Disponible* en un chip de estado; aquí eso es texto
 *   normal, porque `StatusChip` no pinta chip cuando no hay nada que avisar y un chip verde de
 *   «todo bien» compite con el único acento de la pantalla.
 */

/* ======================================================================== la dirección === */

/**
 * El identificador de la ruta, o `null` si no es un número natural. Con `null` no se pide nada:
 * `/api/players/NaN` sería un 404 gratuito.
 */
export function idDeLaRuta(raw: string | undefined): number | null {
  if (raw === undefined || !/^\d+$/.test(raw)) return null;
  const id = Number.parseInt(raw, 10);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

/* ============================================================== la temporada, jornada a === */

/** Los tres casos que puede tener una jornada. La aceptación exige distinguir los tres. */
export type ClaseDeJornada = "jugada" | "no-jugada" | "sin-partido";

/** Cómo se lee cada caso, en la leyenda y en el globo de la gráfica. */
export const CLASES_DE_JORNADA: readonly {
  clase: ClaseDeJornada;
  label: string;
}[] = [
  { clase: "jugada", label: "Jugó" },
  { clase: "no-jugada", label: "No jugó" },
  { clase: "sin-partido", label: "Su equipo no jugó" },
];

const NOMBRE_DE_CLASE: Record<ClaseDeJornada, string> = {
  jugada: "Jugó",
  "no-jugada": "No jugó",
  "sin-partido": "Su equipo no jugó",
};

/**
 * `played` y `teamPlayed` son dos columnas distintas del modelo (sección 5.1) y por eso hay tres
 * casos y no dos. Jugar implica que su equipo jugó, así que `played` manda: si el origen se
 * contradijera, la jornada se cuenta como jugada y no como un partido que no existió.
 */
export function claseDeJornada(round: PlayerRound): ClaseDeJornada {
  if (round.played) return "jugada";
  return round.teamPlayed ? "no-jugada" : "sin-partido";
}

/** Una barra de la gráfica de jornadas. */
export interface PuntoDeJornada {
  /** Etiqueta del eje: el número de jornada, o su posición si viene desdoblada y sin número. */
  jornada: string;
  /** La altura de la barra. Sin puntos —no jugó— es cero, y la forma es la que lo dice. */
  puntos: number;
  /** Lo que dice el servidor, que puede ser `null` aunque haya jugado. */
  reales: number | null;
  clase: ClaseDeJornada;
}

export function datosDeJornadas(
  rounds: readonly PlayerRound[],
): PuntoDeJornada[] {
  return rounds.map((round, indice) => ({
    jornada:
      round.roundNumber > 0 ? String(round.roundNumber) : String(indice + 1),
    puntos: round.points ?? 0,
    reales: round.points,
    clase: claseDeJornada(round),
  }));
}

export interface ResumenDeJornadas {
  total: number;
  jugadas: number;
  noJugadas: number;
  sinPartido: number;
  /** Media de puntos de las jornadas jugadas, o `null` si no jugó ninguna. */
  media: number | null;
}

export function resumenDeJornadas(
  datos: readonly PuntoDeJornada[],
): ResumenDeJornadas {
  const jugadas = datos.filter((uno) => uno.clase === "jugada");
  const suma = jugadas.reduce((total, uno) => total + uno.puntos, 0);

  return {
    total: datos.length,
    jugadas: jugadas.length,
    noJugadas: datos.filter((uno) => uno.clase === "no-jugada").length,
    sinPartido: datos.filter((uno) => uno.clase === "sin-partido").length,
    media: jugadas.length === 0 ? null : suma / jugadas.length,
  };
}

/** La frase que lee quien no ve la gráfica: una gráfica a solas no es accesible. */
export function resumenEscrito(resumen: ResumenDeJornadas): string {
  const partes = [
    `${count(resumen.jugadas)} ${resumen.jugadas === 1 ? "jugada" : "jugadas"}`,
  ];
  if (resumen.media !== null) {
    partes.push(`con una media de ${points(resumen.media)} puntos`);
  }
  if (resumen.noJugadas > 0)
    partes.push(`${count(resumen.noJugadas)} sin jugar`);
  if (resumen.sinPartido > 0) {
    partes.push(`${count(resumen.sinPartido)} sin partido de su equipo`);
  }
  return `${count(resumen.total)} ${
    resumen.total === 1 ? "jornada" : "jornadas"
  }: ${partes.join(", ")}.`;
}

/* ========================================================================= los precios === */

export interface VariacionDePrecio {
  desde: number;
  hasta: number;
  /** Euros de diferencia entre el primer punto de la serie y el último. */
  cambio: number;
  /** Días que cubre la serie, contados por sus fechas. `null` si no se pueden leer. */
  dias: number | null;
}

/** Con menos de dos puntos no hay variación que contar: un precio suelto no es una serie. */
export function variacionDePrecio(
  prices: readonly PricePoint[],
): VariacionDePrecio | null {
  if (prices.length < 2) return null;
  const primera = prices[0];
  const ultima = prices[prices.length - 1];

  return {
    desde: primera.price,
    hasta: ultima.price,
    cambio: ultima.price - primera.price,
    dias: diasEntre(primera.date, ultima.date),
  };
}

/* =========================================================================== el plan ===== */

/** Lo que se puede planear con este jugador: venderlo si es mío, ficharlo si está a la venta. */
export interface OpcionDePlan {
  side: PlanSide;
  /** El precio que entra en el plan: el de catálogo al vender, el de la venta al fichar. */
  price: number;
}

/**
 * La acción correcta según `inSquad` y `listing`, que es el tercer punto de la aceptación.
 *
 *   · Es mío            → *Vender en plan*, al precio de hoy.
 *   · Está a la venta   → *Añadir al plan*, al precio de **la venta**, que es lo que se paga.
 *   · Ninguna de las dos, o la venta ya cerró → nada que planear.
 *
 * Ser mío manda sobre estar a la venta: un jugador de mi plantilla puesto a la venta sigue siendo
 * mío, y lo que puedo planear con él es venderlo, no comprármelo otra vez.
 */
export function opcionDePlan(
  detalle: PlayerDetail,
  ahora: number,
): OpcionDePlan | null {
  if (detalle.inSquad) {
    return { side: "sells", price: detalle.player.price };
  }
  const venta = detalle.listing;
  if (venta === null || haVencido(venta.until, ahora)) return null;
  return { side: "buys", price: venta.listingPrice };
}

/* ============================================================================= piezas ==== */

/** Alto de las dos gráficas, el mismo que reserva `Skeleton variant="chart"` (sección 8.2). */
export const ALTO_DE_GRAFICA = 150;

/** Alto del tocón de una jornada no jugada y de la caja de una jornada sin partido, en píxeles. */
const ALTO_DEL_TOCON = 6;
const ALTO_DE_LA_CAJA = 14;
/** Ni una barra de cero puntos desaparece del todo: se queda en un filo visible. */
const ALTO_MINIMO = 2;

interface BarraDeJornadaProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: PuntoDeJornada;
}

/**
 * La barra de una jornada, con una forma por caso. Recharts entrega la geometría ya calculada
 * (`x`, `y`, `width`, `height`) y el dato de la fila en `payload`; aquí solo se decide qué se
 * dibuja con ella.
 *
 * El color sale de las clases `fill-*`/`stroke-*` de Tailwind, nunca de un hexadecimal escrito a
 * mano: los tokens de la sección 8.4 se declaran una vez, en `tailwind.config.ts`.
 */
function BarraDeJornada({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  payload,
}: BarraDeJornadaProps) {
  const clase = payload?.clase ?? "jugada";
  // Con puntos negativos Recharts cuelga la barra hacia abajo desde la línea del cero; en
  // cualquier otro caso la levanta desde ella. `base` es esa línea en los dos sentidos.
  const negativa = (payload?.puntos ?? 0) < 0;
  const base = negativa ? y : y + height;
  const comun = {
    "data-testid": "barra-jornada",
    "data-jornada": clase,
    width: Math.max(width, 1),
    rx: 2,
  };

  if (clase === "sin-partido") {
    // Caja vacía de trazo discontinuo: ni siquiera hubo partido que jugar.
    return (
      <rect
        {...comun}
        x={x}
        y={base - ALTO_DE_LA_CAJA}
        height={ALTO_DE_LA_CAJA}
        fill="none"
        strokeDasharray="3 2"
        className="stroke-muted"
      />
    );
  }
  if (clase === "no-jugada") {
    // Tocón: hubo partido y se quedó fuera. Ocupa sitio, para que se vea que la jornada existió.
    return (
      <rect
        {...comun}
        x={x}
        y={base - ALTO_DEL_TOCON}
        height={ALTO_DEL_TOCON}
        className="fill-muted"
      />
    );
  }

  const alto = Math.max(Math.abs(height), ALTO_MINIMO);
  return (
    <rect
      {...comun}
      x={x}
      y={negativa ? base : base - alto}
      height={alto}
      className={negativa ? "fill-down" : "fill-pitch"}
    />
  );
}

interface GloboProps {
  active?: boolean;
  payload?: { payload?: PuntoDeJornada }[];
}

/** El globo de la gráfica de jornadas: la cifra exacta y, si no jugó, el motivo. */
function GloboDeJornada({ active = false, payload }: GloboProps) {
  const dato = payload?.[0]?.payload;
  if (!active || dato === undefined) return null;

  return (
    <div className="rounded-[9px] border border-border bg-surface px-2 py-1.5 text-xs">
      <div className="font-semibold text-ink">Jornada {dato.jornada}</div>
      <div className="font-mono tabular-nums text-muted">
        {dato.clase === "jugada"
          ? `${dato.reales === null ? DASH : points(dato.reales)} puntos`
          : NOMBRE_DE_CLASE[dato.clase]}
      </div>
    </div>
  );
}

/** Cada caso con su forma al lado del nombre: la leyenda repite la señal, no solo el color. */
function LeyendaDeJornadas() {
  return (
    <ul className="flex flex-wrap gap-x-3.5 gap-y-1 text-[11px] text-muted">
      {CLASES_DE_JORNADA.map(({ clase, label }) => (
        <li key={clase} className="flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className={
              clase === "jugada"
                ? "h-2.5 w-2.5 rounded-sm bg-pitch"
                : clase === "no-jugada"
                  ? "h-1.5 w-2.5 rounded-sm bg-muted"
                  : "h-2.5 w-2.5 rounded-sm border border-dashed border-muted"
            }
          />
          {label}
        </li>
      ))}
    </ul>
  );
}

/** Rótulo de sección dentro de una tarjeta. */
function TituloDeTarjeta({ children }: { children: ReactNode }) {
  return <h2 className="text-base leading-none">{children}</h2>;
}

function Tarjeta({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={clsx(
        "grid gap-2 rounded-xl border border-border bg-surface px-3 py-2.5",
        className,
      )}
    >
      {children}
    </section>
  );
}

/**
 * Puntos por jornada. `overflow-hidden` en el hueco de la gráfica es el cinturón de seguridad de
 * la sección 8.1: pase lo que pase con la medida, la página no se desplaza en horizontal.
 */
function GraficaDeJornadas({ rounds }: { rounds: readonly PlayerRound[] }) {
  const datos = useMemo(() => datosDeJornadas(rounds), [rounds]);
  const resumen = useMemo(() => resumenDeJornadas(datos), [datos]);
  const minimo = Math.min(0, ...datos.map((uno) => uno.puntos));

  return (
    <Tarjeta>
      <TituloDeTarjeta>Puntos por jornada</TituloDeTarjeta>
      {datos.length === 0 ? (
        <EmptyState
          title="Todavía sin jornadas"
          message="Sincroniza desde Inicio para traer su temporada."
        />
      ) : (
        <>
          <div
            className="min-w-0 overflow-hidden"
            style={{ height: `${String(ALTO_DE_GRAFICA)}px` }}
          >
            <ResponsiveContainer width="100%" height={ALTO_DE_GRAFICA}>
              <BarChart
                data={datos}
                margin={{ top: 6, right: 2, bottom: 0, left: 2 }}
              >
                <XAxis
                  dataKey="jornada"
                  tickLine={false}
                  axisLine={{ className: "stroke-border" }}
                  tick={{ fontSize: 9, className: "fill-muted font-mono" }}
                  interval="preserveStartEnd"
                  minTickGap={4}
                />
                <YAxis hide domain={[minimo, "auto"]} />
                <Tooltip
                  content={<GloboDeJornada />}
                  cursor={{ className: "fill-white/5" }}
                />
                <Bar
                  dataKey="puntos"
                  name="Puntos"
                  isAnimationActive={false}
                  shape={<BarraDeJornada />}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <LeyendaDeJornadas />
          <p className="sr-only">{resumenEscrito(resumen)}</p>
        </>
      )}
    </Tarjeta>
  );
}

interface GloboDePrecioProps {
  active?: boolean;
  payload?: { payload?: PricePoint }[];
}

function GloboDePrecio({ active = false, payload }: GloboDePrecioProps) {
  const dato = payload?.[0]?.payload;
  if (!active || dato === undefined) return null;

  return (
    <div className="rounded-[9px] border border-border bg-surface px-2 py-1.5 text-xs">
      <div className="font-semibold text-ink">{fechaDia(dato.date)}</div>
      <div className="font-mono tabular-nums text-money">
        {money(dato.price)}
      </div>
    </div>
  );
}

/**
 * Histórico de precio. El eje de fechas no se dibuja dentro de la gráfica: se escriben el primer
 * día y el último debajo, uno a cada extremo. Una etiqueta de Recharts en el borde se recorta a
 * 360 px, y con catorce puntos el eje entero es ilegible.
 */
function GraficaDePrecios({
  prices,
  trend7d,
}: {
  prices: readonly PricePoint[];
  trend7d: number | null;
}) {
  const datos = useMemo(() => [...prices], [prices]);
  const variacion = variacionDePrecio(datos);

  return (
    <Tarjeta>
      <TituloDeTarjeta>Valor de mercado</TituloDeTarjeta>
      {variacion === null ? (
        <EmptyState
          title="Todavía sin histórico de precios"
          message="Se guarda un precio por día: sincroniza para empezar la serie."
        />
      ) : (
        <>
          <div
            className="min-w-0 overflow-hidden"
            style={{ height: `${String(ALTO_DE_GRAFICA)}px` }}
          >
            <ResponsiveContainer width="100%" height={ALTO_DE_GRAFICA}>
              <LineChart
                data={datos}
                margin={{ top: 6, right: 6, bottom: 2, left: 6 }}
              >
                <XAxis dataKey="date" hide />
                <YAxis hide domain={["dataMin", "dataMax"]} />
                <Tooltip content={<GloboDePrecio />} />
                <Line
                  type="monotone"
                  dataKey="price"
                  name="Precio"
                  className="stroke-pitch"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3.5, className: "fill-pitch stroke-pitch" }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="flex justify-between font-mono text-[11px] tabular-nums text-muted">
            <span>{fechaDia(datos[0].date)}</span>
            <span>{fechaDia(datos[datos.length - 1].date)}</span>
          </div>

          <p className="text-xs text-muted">
            <span
              className={
                variacion.cambio < 0
                  ? "font-mono tabular-nums text-down"
                  : "font-mono tabular-nums text-up"
              }
            >
              {delta(variacion.cambio)}
            </span>
            {variacion.dias === null
              ? " en el histórico"
              : ` en ${count(variacion.dias)} ${
                  variacion.dias === 1 ? "día" : "días"
                }`}
            {trend7d === null ? null : (
              <>
                {" · "}
                <span
                  className={
                    trend7d < 0
                      ? "font-mono tabular-nums text-down"
                      : "font-mono tabular-nums text-up"
                  }
                >
                  {percentDelta(trend7d)}
                </span>
                {" en 7 días"}
              </>
            )}
          </p>
          <p className="sr-only">
            {`Su precio ha pasado de ${money(variacion.desde)} a ${money(
              variacion.hasta,
            )}.`}
          </p>
        </>
      )}
    </Tarjeta>
  );
}

/** Próximo partido de su equipo: jornada, rival, dónde se juega y cuándo. */
function ProximoPartido({
  fixture,
  ahora,
}: {
  fixture: NextFixture | null;
  ahora: number;
}) {
  // Cuánto falta, si falta: un partido ya jugado no tiene cuenta atrás que enseñar.
  const falta = fixture === null ? null : restante(fixture.date, ahora);

  return (
    <Tarjeta>
      <TituloDeTarjeta>Próximo partido</TituloDeTarjeta>
      {fixture === null ? (
        <p className="text-xs text-muted">
          Su equipo no tiene partido en la próxima jornada, o todavía no hay
          calendario. Sincroniza desde Inicio para traerlo.
        </p>
      ) : (
        <div className="flex items-center justify-between gap-2.5">
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">
              Jornada {count(fixture.roundNumber)} · {fixture.opponent.name}
            </p>
            <p className="text-xs text-muted">
              {fixture.home ? "En casa" : "Fuera"} · {fechaCorta(fixture.date)}
              {falta === null ? null : ` · en ${falta}`}
            </p>
          </div>
          {/* El escudo no está: la palabra sí, y no es un color lo que la sostiene. */}
          <span className="shrink-0 rounded-full border border-border bg-white/5 px-2 py-0.5 text-[11px] text-muted">
            {fixture.home ? "Local" : "Visitante"}
          </span>
        </div>
      )}
    </Tarjeta>
  );
}

/** Lo que dice el mercado de él, cuando hay una venta abierta (o cerrada) suya. */
function EnElMercado({ listing, ahora }: { listing: Listing; ahora: number }) {
  const expirada = haVencido(listing.until, ahora);
  const queda = listing.until === null ? null : restante(listing.until, ahora);

  return (
    <Tarjeta>
      <TituloDeTarjeta>En el mercado</TituloDeTarjeta>
      <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1 text-xs text-muted">
        <span className="font-mono text-base tabular-nums text-money">
          {money(listing.listingPrice)}
        </span>
        <span>Lo vende {vendedor(listing)}</span>
        {expirada ? (
          <span className="font-semibold text-down">Expirado</span>
        ) : queda === null ? null : (
          <span className="font-mono tabular-nums">
            <span className="sr-only">Quedan </span>
            {queda}
          </span>
        )}
        {listing.requiresClause ? <EtiquetaClausula /> : null}
      </div>
      {listing.requiresClause ? (
        <p className="text-xs text-muted">
          Solo se puede fichar pagando su cláusula.
        </p>
      ) : null}
    </Tarjeta>
  );
}

/** La rejilla de métricas del motor (sección 6), con la etiqueta corta y el valor. */
function RejillaDeMetricas({ metrics }: { metrics: Metrics | null }) {
  return (
    <Tarjeta>
      <TituloDeTarjeta>Métricas</TituloDeTarjeta>
      {metrics === null ? (
        <EmptyState
          title="Todavía sin métricas"
          message="Se calculan al sincronizar las jornadas. Hazlo desde Inicio."
        />
      ) : (
        <MetricGrid>
          <MetricBadge
            label="Jornadas"
            value={count(metrics.nPlayed)}
            title="Jornadas jugadas esta temporada"
          />
          <MetricBadge
            label="Media"
            value={points(metrics.ppg)}
            title="Puntos por jornada jugada"
          />
          <MetricBadge
            label="Forma"
            value={points(metrics.form)}
            title="Media reciente, con las últimas jornadas pesando más"
          />
          <MetricBadge
            label="Disponib."
            value={percent(metrics.availability)}
            title="Con qué frecuencia está disponible para jugar"
          />
          <MetricBadge
            label="Restante"
            value={points(metrics.seasonExpected)}
            title="Puntos que se le esperan de aquí al final de la temporada"
          />
          <MetricBadge
            label="Eficiencia"
            value={decimal(metrics.efficiency)}
            title="Puntos esperados por cada millón de su precio"
          />
          <MetricBadge
            label="Tend. 7d"
            value={percentDelta(metrics.trend7d)}
            tone={metrics.trend7d < 0 ? "down" : "up"}
            title="Cuánto ha cambiado su precio en los últimos 7 días"
          />
          <MetricBadge
            label="Tend. 14d"
            value={percentDelta(metrics.trend14d)}
            tone={metrics.trend14d < 0 ? "down" : "up"}
            title="Cuánto ha cambiado su precio en los últimos 14 días"
          />
          <MetricBadge
            label="Puntuación"
            value={decimal(metrics.score)}
            title="Nota con la que el recomendador lo compara con los demás"
          />
        </MetricGrid>
      )}
    </Tarjeta>
  );
}

/**
 * La silueta de la ficha mientras llega (sección 8.2): la misma altura y el mismo reparto que va
 * a sustituirla, con `chart` en el hueco de cada gráfica.
 */
function EsqueletoDeLaFicha() {
  return (
    <>
      <div className="grid gap-2.5 rounded-xl border border-border bg-surface px-3 py-2.5">
        <Skeleton variant="text" className="w-3/5" label="Cargando la ficha" />
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((celda) => (
            <div key={celda} className="grid gap-1.5">
              <Skeleton variant="text" className="w-4/5" label="Cargando" />
              <Skeleton
                variant="text"
                className="h-[30px] w-full"
                label="Cargando"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-2 rounded-xl border border-border bg-surface px-3 py-2.5">
        <Skeleton variant="text" className="w-1/3" label="Cargando" />
        <MetricGrid>
          {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((celda) => (
            <Skeleton
              key={celda}
              variant="text"
              className="h-[46px]"
              label="Cargando las métricas"
            />
          ))}
        </MetricGrid>
      </div>

      {["Cargando los puntos por jornada", "Cargando el valor de mercado"].map(
        (etiqueta) => (
          <div
            key={etiqueta}
            className="grid gap-2 rounded-xl border border-border bg-surface px-3 py-2.5"
          >
            <Skeleton variant="text" className="w-2/5" label="Cargando" />
            <Skeleton variant="chart" label={etiqueta} />
          </div>
        ),
      )}
    </>
  );
}

/* =========================================================================== pantalla ==== */

export function Player() {
  const { id: crudo } = useParams<{ id: string }>();
  const id = idDeLaRuta(crudo);

  const ficha = usePlayer(id);
  const plan = usePlan();
  const guardarPlan = useUpdatePlan();

  const detalle = ficha.data;
  const jugador = detalle?.player;

  /**
   * El reloj se lee una vez por pintado, como en *Mercado*: los plazos de una venta se miden en
   * horas y no hace falta un cronómetro para eso.
   */
  const ahora = Date.now();

  const borrador: PlanDraft = toPlanDraft(plan.data);
  const planLeido = plan.data !== undefined;

  /**
   * En qué lado del plan está. `GET /api/plan` es la fuente; mientras no responde vale el `inPlan`
   * de la venta, que basta para decirlo aunque no sirva para editar.
   */
  const lado: PlanSide | null =
    detalle === undefined
      ? null
      : planLeido
        ? planSideOf(borrador, detalle.player.id)
        : detalle.listing?.inPlan === "buy"
          ? "buys"
          : null;

  const opcion = detalle === undefined ? null : opcionDePlan(detalle, ahora);

  /**
   * La única acción de la pantalla (sección 8.4: una acción primaria y las demás subordinadas).
   * Si ya está en el plan siempre se puede sacar de él, incluso cuando la venta ha cerrado
   * mientras tanto: dejarlo dentro sin manera de quitarlo sería una trampa.
   */
  function accionDePlan(): ReactNode {
    if (detalle === undefined) return null;

    if (lado !== null) {
      return (
        <Button
          block
          variant="secondary"
          disabled={!planLeido || guardarPlan.isPending}
          onClick={() => {
            guardarPlan.mutate(
              removeFromPlan(borrador, lado, detalle.player.id),
            );
          }}
        >
          Quitar del plan
        </Button>
      );
    }

    if (opcion === null) return null;

    return (
      <Button
        block
        variant="primary"
        disabled={!planLeido || guardarPlan.isPending}
        onClick={() => {
          guardarPlan.mutate(
            addToPlan(borrador, opcion.side, {
              playerId: detalle.player.id,
              price: opcion.price,
            }),
          );
        }}
      >
        {opcion.side === "sells" ? "Vender en plan" : "Añadir al plan"}
      </Button>
    );
  }

  /** Por qué no hay acción, cuando no la hay. Un hueco vacío no explica nada. */
  function notaSinAccion(): ReactNode {
    if (detalle === undefined || lado !== null || opcion !== null) return null;

    const cerrada =
      detalle.listing !== null && haVencido(detalle.listing.until, ahora);
    return (
      <p className="text-center text-xs text-muted">
        {cerrada
          ? "Su venta ya ha cerrado: no hay nada que añadir al plan."
          : "No está en tu plantilla ni a la venta, así que no hay nada que planear con él."}
      </p>
    );
  }

  /* ------------------------------------------------------------------------ el cuerpo - */

  let cuerpo: ReactNode;

  if (id === null) {
    cuerpo = (
      <EmptyState
        title="Esa ficha no existe"
        message="La dirección no lleva a ningún jugador."
        action={
          <ButtonLink to="/mercado" variant="primary">
            Ir a Mercado
          </ButtonLink>
        }
      />
    );
  } else if (ficha.isPending) {
    cuerpo = <EsqueletoDeLaFicha />;
  } else if (detalle === undefined || jugador === undefined) {
    cuerpo = (
      <ErrorState
        title="No se ha podido cargar la ficha"
        message={describeError(ficha.error)}
        onRetry={() => void ficha.refetch()}
      />
    );
  } else {
    const metricas = jugador.metrics;
    const etiquetaDeEstado = STATUS_LABELS[jugador.status];
    const rango =
      metricas === null || metricas.rankPosition <= 0
        ? null
        : `#${count(metricas.rankPosition)} ${jugador.position}`;

    cuerpo = (
      <>
        <Tarjeta className="gap-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <PlayerAvatar playerId={jugador.id} name={jugador.name} />
            <PositionChip position={jugador.position} />
            <span className="min-w-0 truncate font-medium text-ink">
              {jugador.team === null ? "Sin equipo" : jugador.team.name}
            </span>
            {etiquetaDeEstado === null ? (
              // `StatusChip` no pinta nada cuando no hay nada que avisar, y en una ficha el
              // silencio se confunde con la falta de datos: aquí se escribe.
              <span className="text-xs text-muted">
                {jugador.status === "ok"
                  ? "Disponible"
                  : "Estado sin confirmar"}
              </span>
            ) : (
              <StatusChip status={jugador.status} detail={jugador.statusText} />
            )}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Stat
              label="Pts esperados"
              value={metricas === null ? DASH : points(metricas.expectedPpr)}
              size={30}
            />
            <Stat
              label="Precio"
              value={money(jugador.price)}
              tone="money"
              size={30}
            />
            <Stat label="Rango" value={rango ?? DASH} size={30} />
          </div>

          <p className="text-xs text-muted">
            {POSITION_NAMES[jugador.position]} · Variación de hoy{" "}
            <span
              className={
                jugador.priceIncrement < 0
                  ? "font-mono tabular-nums text-down"
                  : "font-mono tabular-nums text-up"
              }
            >
              {delta(jugador.priceIncrement)}
            </span>
            {jugador.statusText === null ? null : ` · ${jugador.statusText}`}
          </p>
        </Tarjeta>

        <RejillaDeMetricas metrics={metricas} />
        <GraficaDeJornadas rounds={detalle.rounds} />
        <GraficaDePrecios
          prices={detalle.prices}
          trend7d={metricas?.trend7d ?? null}
        />
        <ProximoPartido fixture={detalle.nextFixture} ahora={ahora} />

        {detalle.listing === null ? null : (
          <EnElMercado listing={detalle.listing} ahora={ahora} />
        )}

        {detalle.inSquad ? (
          <p className="text-xs text-muted">
            Está en tu plantilla
            {detalle.listing === null ? "" : " y lo tienes a la venta"}.
          </p>
        ) : null}

        {plan.isError ? (
          <ErrorState
            title="No se ha podido leer el plan"
            message={describeError(plan.error)}
            onRetry={() => void plan.refetch()}
          />
        ) : null}

        {plan.data !== undefined && !plan.data.planValid ? (
          <Banner title="El plan no se sostiene">
            <ul className="grid list-disc gap-1 pl-5">
              {plan.data.reasons.map((motivo) => (
                <li key={motivo}>{motivo}</li>
              ))}
            </ul>
          </Banner>
        ) : null}

        {guardarPlan.isError ? (
          <Banner tone="error" title="No se ha podido guardar el plan">
            {describeError(guardarPlan.error)}
          </Banner>
        ) : null}

        {lado === null ? null : (
          <p className="text-center text-xs font-semibold text-pitch">
            {lado === "sells" ? "Venta en plan" : "Compra en plan"}
          </p>
        )}

        {accionDePlan()}
        {notaSinAccion()}
      </>
    );
  }

  return (
    <Page title={jugador?.name ?? "Ficha de jugador"} back>
      {cuerpo}
    </Page>
  );
}
